import { useTranslation } from '@flex-mf/i18n';
import { useAxios } from '@flex-packages/remote-hooks';
import { useMutation } from '@tanstack/react-query';
import '../apis/file/apis/file-download-controller-api.mjs';
import '../apis/file/apis/file-info-controller-api.mjs';
import '../apis/file/apis/file-restriction-controller-api.mjs';
import '../apis/file/apis/image-download-controller-api.mjs';
import { createTemporaryFileByPreSignedUrlRemote, verifyTemporaryFilePreSignedUrlRemote, createTemporaryFileMultipartPreSignedUrlRemote, verifyTemporaryFileMultipartPreSignedUrlRemote } from '../apis/file/apis/temporary-file-upload-controller-api.mjs';
import { FileSourceType } from '../apis/file/models/file-source-type.mjs';
import { TemporaryFileUploadMethod } from '../apis/file/models/temporary-file-upload-method.mjs';

const useUploadTemporaryFileByPreSignedUrl = (options) => {
  const axios = useAxios().instances.json;
  const { t } = useTranslation();
  return useMutation({
    ...options,
    mutationFn: async ({ file, sourceType, expiredAt, onUploadProgress }) => {
      const [createPreSignedUrl] = createTemporaryFileByPreSignedUrlRemote(axios);
      const {
        data: { fileKey, uploadUrl, uploadMethod }
      } = await createPreSignedUrl({
        temporaryFilePreSignedCreateRequest: {
          name: file.name,
          size: file.size,
          sourceType: FileSourceType[sourceType],
          mimeType: file.type,
          expiredAt: expiredAt?.toISOString()
        }
      });
      if (uploadMethod !== TemporaryFileUploadMethod.PreSigned) {
        console.error(
          `[useUploadTemporaryFileByPreSignedUrl] '${uploadMethod}'\uB294 \uC9C0\uC6D0\uD558\uC9C0 \uC54A\uB294 upload method\uC5D0\uC694.`
        );
        throw new Error(
          t("global.unknown_error_message", { ns: "@flex-services/file" })
        );
      }
      await axios.put(uploadUrl, file, {
        headers: {
          "content-type": file.type,
          ...expiredAt && {
            expires: expiredAt.toUTCString()
          }
        },
        onUploadProgress: ({ progress }) => progress !== void 0 && onUploadProgress?.(progress)
      });
      const [verifyFile] = verifyTemporaryFilePreSignedUrlRemote(axios);
      const { data } = await verifyFile({ fileKey });
      return data;
    }
  });
};
const useUploadTemporaryFileMultipartByPreSignedUrl = (options) => {
  const axios = useAxios().instances.json;
  return useMutation({
    ...options,
    mutationFn: async ({ file, sourceType, onUploadProgress }) => {
      const [createMultipartPreSignedUrl] = createTemporaryFileMultipartPreSignedUrlRemote(axios);
      const {
        data: { fileKey, uploadId, uploadParts }
      } = await createMultipartPreSignedUrl({
        temporaryFileMultipartPreSignedCreateDto: {
          name: file.name,
          size: file.size,
          sourceType: FileSourceType[sourceType],
          mimeType: "multipart/form-data"
        }
      });
      let start = 0;
      let count = 0;
      const uploadResponses = await Promise.all(
        uploadParts.map(async ({ size, uploadUrl, number }) => {
          const end = start + size;
          const { headers } = await axios.put(
            uploadUrl,
            file.slice(start, end),
            {
              headers: {
                "content-type": "multipart/form-data"
              }
            }
          );
          start = end;
          onUploadProgress?.(++count / uploadParts.length);
          return { signature: headers["etag"] ?? "", partNumber: number };
        })
      );
      const [verifyFile] = verifyTemporaryFileMultipartPreSignedUrlRemote(axios);
      const { data } = await verifyFile({
        temporaryFileMultipartPreSignedVerificationDto: {
          uploadId,
          uploadParts: uploadResponses,
          fileKey
        }
      });
      return data;
    }
  });
};

export { useUploadTemporaryFileByPreSignedUrl, useUploadTemporaryFileMultipartByPreSignedUrl };
//# sourceMappingURL=useUploadTemporaryFileByPreSignedUrl.mjs.map
